import React, { useContext } from 'react'

import { Box, Collapse, IconButton, Typography } from '@mui/material'
import {
  IconChevronDown,
  IconChevronRight,
  IconSparkles,
} from '@tabler/icons-react'
import type { NodeViewProps } from '@tiptap/react'
import { ReactNodeViewRenderer, Node as TiptapNode } from '@tiptap/react'
import { NodeViewContent, NodeViewWrapper } from '@tiptap/react'

import ThreadContext from 'src/components/Threads/V2/Provider/ThreadContext'

const wrapperSx = {
  border: '1px solid',
  borderColor: (theme) => theme.palette.divider,
  borderRadius: 1,
  my: 1,
  p: 1,
  backgroundColor: (theme) => theme.palette.background.default,
  '&.ProseMirror-selectednode': {
    outline: '2px solid',
    outlineColor: (theme) => theme.palette.primary.main,
  },
}

const headerSx = {
  display: 'flex',
  alignItems: 'center',
  cursor: 'pointer',
  userSelect: 'none',
  gap: 1,
}

const iconButtonSx = {
  p: 0.5,
}

const contentSx = {
  mt: 1,
  pl: 3,
}

interface ThinkingModeProps extends NodeViewProps {
  node: NodeViewProps['node'] & {
    attrs: {
      model: string
      timestamp: string
      stableId: string
    }
  }
}

const ThinkingModeRenderer: React.FC<ThinkingModeProps> = React.memo(
  (props) => {
    const { state, dispatch } = useContext(ThreadContext)
    const handleToggle = () => {
      dispatch({
        type: state.blockState?.[props.node.attrs.stableId]
          ? 'SET_THINKING_CLOSED'
          : 'SET_THINKING_OPEN',
        payload: props.node.attrs.stableId,
      })
    }

    return (
      <NodeViewWrapper>
        <Box sx={wrapperSx}>
          <Box
            sx={headerSx}
            onClick={handleToggle}
          >
            <IconButton
              size="small"
              onClick={handleToggle}
              sx={iconButtonSx}
            >
              {!state.blockState?.[props.node.attrs.stableId] ? (
                <IconChevronRight size={18} />
              ) : (
                <IconChevronDown size={18} />
              )}
            </IconButton>
            <IconSparkles size={18} />
            <Typography
              variant="caption"
              sx={{ fontWeight: 'medium', flexGrow: 1 }}
            >
              Reasoning
            </Typography>
          </Box>
          <Collapse
            in={state.blockState?.[props.node.attrs.stableId]}
            timeout="auto"
            unmountOnExit
          >
            <Box sx={contentSx}>
              <NodeViewContent className="content" />
            </Box>
          </Collapse>
        </Box>
      </NodeViewWrapper>
    )
  }
)

const ThinkingMode = TiptapNode.create({
  name: 'thinkingMode',
  group: 'block',

  content: 'paragraph+',

  addAttributes() {
    return {
      modelName: {
        default: 'default-model',
      },
      timestamp: {
        default: null,
      },
      stableId: {
        default: null,
      },
    }
  },

  parseHTML() {
    return [
      {
        tag: 'span[data-type="thinkingMode"]',
        getAttrs: (dom) => {
          const id =
            dom instanceof HTMLElement
              ? dom.getAttribute('data-stable-id')
              : null

          return {
            stableId: id,
          }
        },
      },
    ]
  },

  addNodeView() {
    return ReactNodeViewRenderer(ThinkingModeRenderer)
  },

  renderHTML({ HTMLAttributes }) {
    return [
      'span',
      {
        ...HTMLAttributes,
        'data-type': 'thinkingMode',
      },
      0,
    ]
  },
})

export default ThinkingMode
